import { normalizeUTC } from '@/utils/date';
import type {
  AuctionSearchFilters,
  ProductDetails,
  SpotBid,
  SpotQuoteDocument,
} from '@/views/screens/AuctionScreens/types';
import type { ShipmentDetailedItemTrackingApi } from '@/views/screens/Shipment/types';
import { convertDateESTToUTCToUserLocal } from '../../utilz/dates';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import AuctionTypes from './AuctionTypes';

export interface AuctionState extends Record<string, any> {
  spotBidList: DefaultStoreObject<SpotBid[]> & {
    isSearchFirstTime?: boolean;
  };
  spotBidExportList: DefaultStoreObject<SpotBid[]>;
  documentDetails: DefaultStoreObject<SpotQuoteDocument[]>;
  shipmentDetails: DefaultStoreObject<ShipmentDetailedItemTrackingApi>;
  auctionSearchFilters: AuctionSearchFilters;
  spotBidTotalCount: {
    totalCount: number;
  };
  selectedSpotBid: SpotBid;
  insertSpotBidStatus: DefaultStoreObject<null>;
  shipmentProductDetails: DefaultStoreObject<ProductDetails[]>;
}

const initialState: AuctionState = {
  spotBidList: { ...DEFAULT_STORE_OBJECT, data: [], isSearchFirstTime: true },
  spotBidExportList: { ...DEFAULT_STORE_OBJECT, data: [] },
  documentDetails: { ...DEFAULT_STORE_OBJECT, data: [] },
  shipmentDetails: { ...DEFAULT_STORE_OBJECT, data: null },
  auctionSearchFilters: {
    customerKey: '',
    openClose: 'O',
    equipmentType: 0,
    toNumber: '',
    shipmentCreateDateFrom: '',
    shipmentCreateDateTo: '',
    expectedShipmentDateFrom: '',
    expectedShipmentDateTo: '',
    approvalNumber: '',
    pageIndex: 1,
    pageSize: 10,
    orderByColumn: '',
  },
  insertSpotBidStatus: { ...DEFAULT_STORE_OBJECT },
  spotBidTotalCount: {
    totalCount: 0,
  },
  selectedSpotBid: {} as SpotBid,
  shipmentProductDetails: { ...DEFAULT_STORE_OBJECT },
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const AuctionReducer = (state = initialState, { type, payload }: any): AuctionState => {
  switch (type) {
    case AuctionTypes.GET_SPOTBID_SEARCH_RESULTS_PROGRESS: {
      return {
        ...state,
        spotBidList: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: state.spotBidList.data,
          isSearchFirstTime: false,
        },
      };
    }

    case AuctionTypes.GET_SPOTBID_SEARCH_RESULTS_ERROR: {
      return {
        ...state,
        spotBidList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.spotBidList.data || [])],
        },
      };
    }

    case AuctionTypes.GET_SPOTBID_SEARCH_RESULTS_SUCCESS: {
      return {
        ...state,
        spotBidList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [
            ...(state.spotBidList.data || []),
            // @ts-ignore
            ...(payload.data?.map((element) => ({
              ...element,
              expShipDate: convertDateESTToUTCToUserLocal(element.expShipDate),
              expDeliveryDate: convertDateESTToUTCToUserLocal(element.expDeliveryDate),
              effectiveTo: convertDateESTToUTCToUserLocal(element.effectiveTo),
              bidExpirtyDateTime: normalizeUTC(element.bidExpirtyDateTime, 'MM/dd/yyyy h:mm:ss a'),
            })) ?? []),
          ],
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_SEARCH_RESULTS: {
      return {
        ...state,
        spotBidList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.spotBidList,
          ...payload,
        },
      };
    }

    case AuctionTypes.SET_SEARCH_FILTERS: {
      return {
        ...state,
        auctionSearchFilters: {
          ...state.auctionSearchFilters,
          ...payload,
        },
      };
    }

    case AuctionTypes.GET_SHIPMENT_PRODUCT_DETAIL_PROGRESS: {
      return {
        ...state,
        shipmentProductDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload,
        },
      };
    }

    case AuctionTypes.GET_SHIPMENT_PRODUCT_DETAIL_ERROR: {
      return {
        ...state,
        shipmentProductDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.shipmentProductDetails.data || [])],
        },
      };
    }

    case AuctionTypes.GET_SHIPMENT_PRODUCT_DETAIL_SUCCESS: {
      return {
        ...state,
        shipmentProductDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_SAVE_PROGRESS: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_SAVE_SUCCESS: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_SAVE_ERROR: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_DECLINE_PROGRESS: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_DECLINE_SUCCESS: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_DECLINE_ERROR: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_SAVE_STATUS_DEFAULT: {
      return {
        ...state,
        insertSpotBidStatus: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }

    case AuctionTypes.GET_SPOTBID_EXPORT_SEARCH_RESULTS_PROGRESS: {
      return {
        ...state,
        spotBidExportList: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: state.spotBidExportList.data,
        },
      };
    }

    case AuctionTypes.GET_SPOTBID_EXPORT_SEARCH_RESULTS_ERROR: {
      return {
        ...state,
        spotBidExportList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.spotBidExportList.data || [])],
        },
      };
    }

    case AuctionTypes.GET_SPOTBID_EXPORT_SEARCH_RESULTS_SUCCESS: {
      return {
        ...state,
        spotBidExportList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [...payload.data],
        },
      };
    }

    case AuctionTypes.GET_SPOTBID_LIST_SUMBIT_SUCCESS: {
      return {
        ...state,
        spotBidList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: state.spotBidList.data?.map((item) => {
            // @ts-ignore
            const updatedItem = payload.data.find((newItem) => newItem.shipmentKey === item.shipmentKey);
            if (updatedItem) {
              return {
                ...item,
                ...updatedItem,
                expShipDate: convertDateESTToUTCToUserLocal(updatedItem.expShipDate),
                expDeliveryDate: convertDateESTToUTCToUserLocal(updatedItem.expDeliveryDate),
                effectiveTo: convertDateESTToUTCToUserLocal(updatedItem.effectiveTo),
                bidExpirtyDateTime: normalizeUTC(updatedItem.bidExpirtyDateTime, 'MM/dd/yyyy h:mm:ss a'),
              };
            }
            return item;
          }),
        },
      };
    }

    case AuctionTypes.SET_SPOTBID_TOTAL_COUNT: {
      return {
        ...state,
        spotBidTotalCount: { ...payload },
      };
    }

    case AuctionTypes.SET_SELECTED_SPOTBID: {
      return {
        ...state,
        selectedSpotBid: { ...payload },
      };
    }

    case AuctionTypes.SET_SPOTBID_VALUE_DEFAULT: {
      return {
        ...state,
        spotBidList: {
          ...DEFAULT_STORE_OBJECT,
          isSearchFirstTime: true,
          ...payload,
        },
      };
    }

    case AuctionTypes.GET_DOCUMENTS_PROGRESS: {
      return {
        ...state,
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case AuctionTypes.GET_DOCUMENTS_SUCCESS: {
      return {
        ...state,
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuctionTypes.GET_DOCUMENTS_ERROR: {
      return {
        ...state,
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case AuctionTypes.GET_MULTI_QUOTE_PROGRESS: {
      return {
        ...state,
        shipmentDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.data,
        },
      };
    }

    case AuctionTypes.GET_MULTI_QUOTE_ERROR: {
      return {
        ...state,
        shipmentDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case AuctionTypes.GET_MULTI_QUOTE_SUCCESS: {
      return {
        ...state,
        shipmentDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    default:
      return state;
  }
};

export default AuctionReducer;
